import React, { useCallback, useEffect } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import useMe from "../../../scenes/auth/hooks/useMe";
import Image from "../../common/Image";
import { IconThemeWhoppahSpark } from "@/assets/icons/figma/IconThemeWhoppahSpark";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import { ACCOUNT_ADS_ROUTE } from "@/common/static/routes.static";
import LokaliseMarkdownText from "@/components/text/LokaliseMarkdownText";
import Modal, { handleModalClose, showModal } from "@/ui/Modal2";
import { Button } from "@/ui/cva/Button";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import useLazyAdsAboutToExpire from "@whoppah/apollo/hooks/useLazyAdsAboutToExpire";

const AD_ABOUT_TO_EXPIRE_MESSAGE_TIMESTAMP_KEY =
  "ad_about_to_expire_message_timestamp";

const FIVE_HOURS_IN_MILLISECONDS = 18000000;

type AdIsAboutToExpireModalProps = {
  ad: ProductUnknown;
};

const AdIsAboutToExpireModal = NiceModal.create(
  ({ ad }: AdIsAboutToExpireModalProps) => {
    const { t } = useTranslation();

    const modal = useModal();

    const id = ad.id;
    const name = ad.title;

    return (
      <Modal open={modal.visible} onClose={handleModalClose(modal)}>
        <div className="-mt-[48px] flex max-w-[640px] flex-col items-center gap-[24px] md:px-[64px]">
          <div className="flex flex-col items-center">
            <IconThemeWhoppahSpark className="size-12 text-brand-500" />
            <div className="h-[192px] w-[192px] rounded-[4px] p-[16px] shadow-md">
              <div className="relative h-full w-full">
                <Image
                  src={ad.image?.url}
                  alt="Photo"
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-[16px]">
            <span className="font-poppins text-[16px] font-semibold md:text-[24px]">
              {t("common:ad-about-to-expire-title")}
            </span>
            <span className="text-center">
              <LokaliseMarkdownText
                wrapper="span"
                lokaliseKey="common:ad-about-to-expire"
                options={{ name, interpolation: { escapeValue: false } }}
              />
              <span>&nbsp;</span>
              <Link
                href={`${ACCOUNT_ADS_ROUTE}?status=EXPIRING`}
                prefetch={false}
                className="text-brand-500 underline focus:outline-none"
                onClick={handleModalClose(modal)}
              >
                {t("common:your-ads-title")}
              </Link>
            </span>
          </div>
          <div className="mt-[16px] grid w-full gap-2 md:grid-cols-2 md:gap-4">
            <Button
              appearance="outlined"
              onClick={handleModalClose(modal)}
              className="order-2 md:order-1"
            >
              {t("common:common-close")}
            </Button>
            <LinkButton
              href={`${ACCOUNT_ADS_ROUTE}/${id}`}
              onClick={handleModalClose(modal)}
              className="order-1 md:order-2"
            >
              {t("common:ad-reactivate")}
            </LinkButton>
          </div>
        </div>
      </Modal>
    );
  }
);

const useAdIsAboutToExpire = () => {
  const { data: meData } = useMe();

  // We use lazy query because we want full control of when we are fetching data.
  const [getAds] = useLazyAdsAboutToExpire();

  const getAdsFunc = useCallback(
    async (merchantId: string) => {
      const now = Date.now();

      /**
       * Update the timestamp, even if we will have expired ads or not.
       * This will postpone the next fetch for 5 hours.
       */
      window.localStorage.setItem(
        AD_ABOUT_TO_EXPIRE_MESSAGE_TIMESTAMP_KEY,
        `${now + FIVE_HOURS_IN_MILLISECONDS}`
      );

      const ads = await getAds({
        variables: { merchantId },
      });

      if (ads?.data?.products?.items?.length > 0) {
        showModal(AdIsAboutToExpireModal, { ad: ads.data.products.items[0] });
      }
    },
    [getAds]
  );

  useEffect(() => {
    const now = Date.now();

    const timestamp = parseInt(
      window.localStorage.getItem(AD_ABOUT_TO_EXPIRE_MESSAGE_TIMESTAMP_KEY) ??
        "0"
    );

    const diff = timestamp - now;

    if (diff <= 0) {
      // Fetch ads that are about expire, if any.
      const merchantId = meData?.me?.merchants?.[0]?.id;
      if (merchantId) {
        getAdsFunc(merchantId);
      }
    }
  }, [getAds, getAdsFunc, meData]);
};

export default useAdIsAboutToExpire;
